import { createBrowserRouter, Navigate } from 'react-router';
import { AdminPage } from '@/features/admin/AdminPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { RequireAnonymous, RequireAuth } from '@/features/auth/RequireAuth';
import { SelectOrganizationPage } from '@/features/auth/SelectOrganizationPage';
import { ClientDetailPage } from '@/features/clients/ClientDetailPage';
import { ClientsPage } from '@/features/clients/ClientsPage';
import { ContextsPage } from '@/features/contexts/ContextsPage';
import { MyWorkPage } from '@/features/my-work/MyWorkPage';
import { NoteEditorPage } from '@/features/notes/NoteEditorPage';
import { NotePage } from '@/features/notes/NotePage';
import { NotesPage } from '@/features/notes/NotesPage';
import { PersonalTasksPage } from '@/features/personal/PersonalTasksPage';
import { AppearancePage } from '@/features/settings/AppearancePage';
import { ProfilePage } from '@/features/settings/ProfilePage';
import { NotificationsSettingsPage } from '@/features/settings/NotificationsSettingsPage';
import { PipelineEditorPage } from '@/features/pipelines/PipelineEditorPage';
import { PipelinesPage } from '@/features/pipelines/PipelinesPage';
import { TaskDetailPage } from '@/features/tasks/TaskDetailPage';
import { TasksListPage } from '@/features/tasks/TasksListPage';
import { TeamsPage } from '@/features/teams/TeamsPage';
import { WorkflowEditorPage } from '@/features/workflows/WorkflowEditorPage';
import { WorkflowsPage } from '@/features/workflows/WorkflowsPage';
import { FormEditorPage } from '@/features/forms/FormEditorPage';
import { FormsPage } from '@/features/forms/FormsPage';
import { PublicFormPage } from '@/features/forms/PublicFormPage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { RequestDetailPage } from '@/features/inbox/RequestDetailPage';
import { CalendarPage } from '@/features/calendar/CalendarPage';
import { CommercialPage } from '@/features/commercial/CommercialPage';
import { ContractPage } from '@/features/commercial/ContractPage';
import { ProposalPage } from '@/features/commercial/ProposalPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { WeekPage } from '@/features/dashboard/WeekPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { PortalInvitePage, PortalLoginPage } from '@/portal/PortalAuthPages';
import {
  PortalFormPage,
  PortalFormsPage,
  PortalRequestsPage,
  PortalTaskPage,
  PortalTasksPage,
} from '@/portal/PortalPages';
import { PortalProvider, PortalShell } from '@/portal/PortalShell';
import { AppShell } from './AppShell';

export const router = createBrowserRouter([
  // Página pública do formulário: sem layout autenticado, sem prioridade (FRONTEND_UX §11).
  { path: '/f/:slug', element: <PublicFormPage /> },
  // Portal do cliente (Fase 7): sessão própria do contato, fora do app interno.
  {
    element: <PortalProvider />,
    children: [
      { path: '/portal/login', element: <PortalLoginPage /> },
      { path: '/portal/convite/:token', element: <PortalInvitePage /> },
      {
        element: <PortalShell />,
        children: [
          { path: '/portal', element: <PortalTasksPage /> },
          { path: '/portal/demandas/:id', element: <PortalTaskPage /> },
          { path: '/portal/solicitacoes', element: <PortalRequestsPage /> },
          { path: '/portal/solicitar', element: <PortalFormsPage /> },
          { path: '/portal/solicitar/:formId', element: <PortalFormPage /> },
        ],
      },
    ],
  },
  {
    element: <RequireAnonymous />,
    children: [{ path: '/login', element: <LoginPage /> }],
  },
  {
    element: <RequireAuth />,
    children: [
      { path: '/select-organization', element: <SelectOrganizationPage /> },
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/my-work" replace /> },
          { path: '/my-work', element: <MyWorkPage /> },
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/semana', element: <WeekPage /> },
          { path: '/demandas', element: <TasksListPage /> },
          { path: '/pipelines', element: <PipelinesPage /> },
          { path: '/pipelines/novo', element: <PipelineEditorPage /> },
          { path: '/pipelines/:id/editar', element: <PipelineEditorPage /> },
          // Endereço antigo (favoritos): o Kanban virou Pipelines.
          { path: '/kanban', element: <Navigate to="/pipelines" replace /> },
          { path: '/tasks/:id', element: <TaskDetailPage /> },
          { path: '/clientes', element: <ClientsPage /> },
          { path: '/clientes/:id', element: <ClientDetailPage /> },
          { path: '/contextos', element: <ContextsPage /> },
          { path: '/times', element: <TeamsPage /> },
          { path: '/workflows', element: <WorkflowsPage /> },
          { path: '/workflows/:id', element: <WorkflowEditorPage /> },
          { path: '/calendario', element: <CalendarPage /> },
          { path: '/formularios', element: <FormsPage /> },
          { path: '/formularios/:id', element: <FormEditorPage /> },
          { path: '/comercial', element: <CommercialPage /> },
          { path: '/comercial/propostas/:id', element: <ProposalPage /> },
          { path: '/comercial/contratos/:id', element: <ContractPage /> },
          { path: '/triagem', element: <InboxPage /> },
          { path: '/triagem/:id', element: <RequestDetailPage /> },
          { path: '/relatorios', element: <ReportsPage /> },
          { path: '/admin', element: <AdminPage /> },
          { path: '/pessoal', element: <PersonalTasksPage /> },
          { path: '/anotacoes', element: <NotesPage /> },
          { path: '/anotacoes/nova', element: <NoteEditorPage /> },
          { path: '/anotacoes/:id', element: <NotePage /> },
          { path: '/anotacoes/:id/editar', element: <NoteEditorPage /> },
          { path: '/settings/profile', element: <ProfilePage /> },
          { path: '/settings/appearance', element: <AppearancePage /> },
          { path: '/settings/notifications', element: <NotificationsSettingsPage /> },
          { path: '*', element: <Navigate to="/my-work" replace /> },
        ],
      },
    ],
  },
]);
